// Kuka — a törölt cikkek listája és visszaállítása.
//
// A Hubban a törlés nem semmisít meg semmit: a busz-sor megmarad `deleted` státusszal. Eddig
// viszont sehol nem lehetett listázni őket, tehát a „Kukába" gomb a gyakorlatban mégis
// visszafordíthatatlan volt. Ez az oldal zárja be ezt a kört.
//
// A visszaállítás VÁZLATKÉNT hoz vissza — a publikálás külön döntés, nem a törlés visszavonásának
// mellékhatása.
import { ArrowUturnLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PERMISSIONS } from '@morgens-hub/shared';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AppShell } from '@/components/shared/app-shell';
import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRestoreFromTrash, useTrashList } from '@/features/editor/api';
import { useInvalidateKnowledge } from '@/features/knowledge/api';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useErrorText } from '@/lib/use-error-text';
import { useHasPermission } from '@/lib/use-permission';

export const Route = createFileRoute('/kuka')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: TrashPage,
});

const dateFormat = new Intl.DateTimeFormat('hu-HU', { dateStyle: 'medium', timeStyle: 'short' });

function TrashPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const session = api.session.useQuery(['session']);
  const trash = useTrashList();
  const restore = useRestoreFromTrash();
  const invalidateKnowledge = useInvalidateKnowledge();
  const canDelete = useHasPermission(PERMISSIONS.articleDelete);

  const sessionBody = session.data?.status === 200 ? session.data.body : null;
  const body = trash.data?.status === 200 ? trash.data.body : null;

  return (
    <AppShell session={sessionBody}>
      <div className="mx-auto max-w-3xl">
        <KnowledgeCrumbs current={t('trash.title')} />
        <header>
          <h1 className="text-read-2xl font-semibold tracking-tight">{t('trash.title')}</h1>
          <p className="text-muted-foreground text-read-sm mt-2">{t('trash.subtitle')}</p>
        </header>

        {trash.isLoading && <Skeleton className="mt-8 h-40 w-full" />}

        {!trash.isLoading && !body && (
          <p className="text-destructive mt-8 text-sm">{t('trash.loadError')}</p>
        )}

        {body?.articles.length === 0 && (
          <div className="text-muted-foreground mt-10 flex items-center gap-3 text-sm">
            <TrashIcon className="text-subtle h-5 w-5" aria-hidden />
            {t('trash.empty')}
          </div>
        )}

        {body && body.articles.length > 0 && (
          <ul className="divide-border border-border mt-8 divide-y border-y">
            {body.articles.map((article) => (
              <li key={article.id} className="flex flex-wrap items-center gap-4 py-3.5">
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{article.title}</span>
                  <span className="text-subtle mt-0.5 block text-sm">
                    {article.spaceName || t('trash.unknownPlace')}
                    {article.categoryName && (
                      <>
                        {' '}
                        <span aria-hidden>›</span> {article.categoryName}
                      </>
                    )}
                    <span aria-hidden> · </span>
                    {t('trash.deletedAt', { date: dateFormat.format(new Date(article.updatedAt)) })}
                  </span>
                </span>

                {canDelete && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1.5"
                    disabled={restore.isPending}
                    onClick={() => {
                      restore.mutate(
                        { params: { id: article.id }, body: { version: article.version } },
                        {
                          onSuccess: () => {
                            toast.success(t('articleRestored', { ns: 'toast' }));
                            void trash.refetch();
                            invalidateKnowledge();
                          },
                          onError: (failure: unknown) => {
                            // Az OCC-ütközés (más nyúlt a cikkhez) a `conflict` kód üzenetét kapja;
                            // minden más ok a sajátját — a státusz-kapcsoló helyére a kód-alapú
                            // rendszer lépett.
                            toast.error(errorText(failure, t('trash.restoreOperation')));
                            void trash.refetch();
                          },
                        },
                      );
                    }}
                  >
                    <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden />
                    {t('trash.restore')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        <p className="text-subtle mt-8 text-sm">
          {t('trash.hint')}{' '}
          <Link to="/tudastar" className="hover:text-foreground underline underline-offset-2">
            {t('knowledge.title')}
          </Link>
        </p>
      </div>
    </AppShell>
  );
}
